/**
 * CSRF token utilities.
 *
 * The token is sent via the X-CSRF-Token request header (injected automatically
 * by apiClient.js). Real CSRF protection requires the backend to independently
 * validate this token on every mutation (POST/PUT/PATCH/DELETE).
 *
 * NOTE: Client-side token comparison was removed — comparing a value we generated
 * against a header the server echoes back provides no security (circular validation).
 * Proper protection: backend issues tokens via httpOnly session/cookie and validates
 * them server-side before processing requests.
 */

/**
 * Generate a cryptographically random CSRF token.
 */
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Get the current CSRF token from localStorage, or generate and store a new one.
 */
export const getCSRFToken = () => {
  let token = localStorage.getItem('csrf-token');
  if (!token) {
    token = generateCSRFToken();
    localStorage.setItem('csrf-token', token);
  }
  return token;
};

/**
 * Return a headers object with the CSRF token added.
 * Used by code that builds headers manually (outside of apiClient interceptors).
 */
export const addCSRFToken = (headers = {}) => {
  return {
    ...headers,
    'X-CSRF-Token': getCSRFToken(),
    'Content-Type': 'application/json',
  };
};
